import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle, 
  Circle, 
  BarChart3,
  Target,
  Users,
  TrendingUp,
  Info,
  Eye,
  Hand,
  Heart,
  Ruler,
  Scale,
  Utensils,
  Church,
  MapPin,
  Cigarette,
  Droplets
} from 'lucide-react';

interface AuthentemiaQuestion {
  id: string;
  label: string;
  exclusive: boolean;
  options: {
    value: string;
    p_global: number;
    source: {
      name: string;
      year: number;
      url: string;
    };
    method_note?: string;
  }[];
}

interface AuthentemiaData {
  schema_version: string;
  notes: string;
  questions: AuthentemiaQuestion[];
}

interface UserAnswers {
  [questionId: string]: string;
}

const Authentemia: React.FC = () => {
  const [, navigate] = useLocation();
  const [data, setData] = useState<AuthentemiaData | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswers>({});
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/authentemia/questions');
      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }
      const questionsData = await response.json();
      setData(questionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId: string, value: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    // Auto-advance to next question after a short delay (except for last question)
    setTimeout(() => {
      if (currentQuestionIndex < (data?.questions.length || 0) - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      }
      // Don't auto-advance on last question, user needs to click "Finalizar"
    }, 500); // 500ms delay for visual feedback
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const calculateAuthenticityScore = (): { score: number; rarity: number; details: any[] } => {
    if (!data || !userAnswers) return { score: 0, rarity: 0, details: [] };

    const details: any[] = [];
    let totalRarity = 0;
    let answeredQuestions = 0;

    data.questions.forEach(question => {
      const answer = userAnswers[question.id];
      if (answer) {
        const option = question.options.find(opt => opt.value === answer);
        if (option && option.p_global > 0) {
          const probability = option.p_global / 100;
          const rarity = 1 / probability; // Cuántas personas hay por cada una como tú
          
          totalRarity += rarity;
          answeredQuestions++;
          
          details.push({
            question: question.label,
            answer: answer,
            probability: option.p_global,
            rarity: rarity
          });
        }
      }
    });

    if (answeredQuestions === 0) {
      return { score: 0, rarity: 0, details: [] };
    }

    // Calcular la rareza promedio
    const averageRarity = totalRarity / answeredQuestions;
    
    // Convertir a un score de 0-100 más lógico
    // Si eres muy común (rarity < 10), score bajo
    // Si eres muy raro (rarity > 1000), score alto
    let authenticityScore;
    if (averageRarity <= 10) {
      // Muy común: 0-20%
      authenticityScore = (averageRarity / 10) * 20;
    } else if (averageRarity <= 100) {
      // Común a poco común: 20-60%
      authenticityScore = 20 + ((averageRarity - 10) / 90) * 40;
    } else if (averageRarity <= 1000) {
      // Poco común a raro: 60-90%
      authenticityScore = 60 + ((averageRarity - 100) / 900) * 30;
    } else {
      // Muy raro: 90-100%
      authenticityScore = 90 + Math.min((averageRarity - 1000) / 10000, 10);
    }

    return {
      score: Math.round(authenticityScore * 100) / 100,
      rarity: Math.round(averageRarity),
      details
    };
  };

  const getQuestionIcon = (questionId: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      sex_assigned_at_birth: <Users size={24} />,
      dominant_hand: <Hand size={24} />,
      eye_color: <Eye size={24} />,
      color_vision_deficiency: <Eye size={24} />,
      myopia: <Eye size={24} />,
      height_range_cm: <Ruler size={24} />,
      bmi_category: <Scale size={24} />,
      diet_pattern: <Utensils size={24} />,
      religion: <Church size={24} />,
      residence_area: <MapPin size={24} />,
      tobacco_use: <Cigarette size={24} />,
      blood_type_abo: <Droplets size={24} />
    };
    return iconMap[questionId] || <Target size={24} />;
  };

  const getProgressPercentage = () => {
    if (!data) return 0;
    return ((currentQuestionIndex + 1) / data.questions.length) * 100;
  };

  const getAnsweredQuestionsCount = () => {
    return Object.keys(userAnswers).length;
  };

  const getCurrentProgress = () => {
    if (!data) return { score: 0, peopleCount: 0, answeredCount: 0 };
    
    const answeredCount = Object.keys(userAnswers).length;
    if (answeredCount === 0) return { score: 0, peopleCount: 8000000000, answeredCount: 0 };
    
    // Calculate current score based on answered questions only
    const details: any[] = [];
    let totalRarity = 0;
    
    data.questions.forEach(question => {
      const answer = userAnswers[question.id];
      if (answer) {
        const option = question.options.find(opt => opt.value === answer);
        if (option && option.p_global > 0) {
          const probability = option.p_global / 100;
          const rarity = 1 / probability;
          totalRarity += rarity;
          
          details.push({
            question: question.label,
            answer: answer,
            probability: option.p_global,
            rarity: rarity
          });
        }
      }
    });
    
    const averageRarity = totalRarity / answeredCount;
    
    // Convert to score (same logic as final calculation)
    let score;
    if (averageRarity <= 10) {
      score = (averageRarity / 10) * 20;
    } else if (averageRarity <= 100) {
      score = 20 + ((averageRarity - 10) / 90) * 40;
    } else if (averageRarity <= 1000) {
      score = 60 + ((averageRarity - 100) / 900) * 30;
    } else {
      score = 90 + Math.min((averageRarity - 1000) / 10000, 10);
    }
    
    // Calculate how many people could be like this
    const worldPopulation = 8000000000; // 8 billion
    const peopleCount = Math.round(worldPopulation / averageRarity);
    
    return {
      score: Math.round(score * 100) / 100,
      peopleCount,
      answeredCount
    };
  };

  if (loading) {
    return (
      <div className="statemia-container">
        <div className="statemia-card">
          <div className="text-center py-12">
            <div className="statemia-spinner mx-auto mb-4" />
            <p>Cargando test de autenticidad...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="statemia-container">
        <div className="statemia-card">
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Error al cargar el test
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-primary"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const { score, rarity, details } = calculateAuthenticityScore();
    
    return (
      <div className="statemia-container">
        {/* Header */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
          <div className="statemia-container">
            <div className="flex items-center justify-between py-4">
              <button
                onClick={() => setShowResults(false)}
                className="btn-secondary flex items-center gap-2"
              >
                <ArrowLeft size={16} />
                Volver al test
              </button>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setUserAnswers({});
                    setCurrentQuestionIndex(0);
                    setShowResults(false);
                  }}
                  className="btn-primary flex items-center gap-2"
                >
                  <BarChart3 size={16} />
                  Nuevo Test
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Spacer for fixed header */}
        <div className="h-20"></div>

        {/* Results */}
        <div className="statemia-card mb-6">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Tu Puntuación de Autenticidad
            </h1>
            <p className="text-xl text-muted-foreground">
              Basado en {details.length} características demográficas
            </p>
          </div>

          {/* Main Score */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200">
              <div className="text-6xl font-bold text-blue-600 mb-2">
                {score}%
              </div>
              <div className="text-lg font-semibold text-blue-800 mb-2">
                Puntuación de Autenticidad
              </div>
              <p className="text-blue-600">
                {score < 20 ? 'Muy común' : 
                 score < 60 ? 'Común' : 
                 score < 90 ? 'Poco común' : 'Muy raro'}
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
              <div className="text-6xl font-bold text-purple-600 mb-2">
                1 en {rarity.toLocaleString()}
              </div>
              <div className="text-lg font-semibold text-purple-800 mb-2">
                Raridad Global
              </div>
              <p className="text-purple-600">
                {rarity < 10 ? 'Muy común' : 
                 rarity < 100 ? 'Común' : 
                 rarity < 1000 ? 'Poco común' : 'Muy raro'}
              </p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Detalles por Característica
            </h3>
            {details.map((detail, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-surface-50 rounded-lg border">
                <div className="flex-1">
                  <div className="font-semibold text-foreground">{detail.question}</div>
                  <div className="text-sm text-muted-foreground">{detail.answer}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-primary">{detail.probability}%</div>
                  <div className="text-xs text-muted-foreground">
                    1 en {Math.round(detail.rarity).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Share Section */}
          <div className="mt-8 pt-8 border-t border-surface-300">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-foreground mb-2">
                ¡Comparte tu resultado!
              </h3>
              <p className="text-muted-foreground">
                Descubre qué tan único eres y compártelo con tus amigos
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {/* Twitter/X */}
              <button
                onClick={() => {
                  // console.log('[LOG_RSS] Authentemia Twitter share button clicked'); // Removed for production
                  const text = `🎯 Mi puntuación de autenticidad es ${score}% - ¡Soy 1 en ${rarity.toLocaleString()} personas! Descubre qué tan único eres en @statemia_app`;
                  const url = encodeURIComponent(window.location.href);
                  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${url}&hashtags=Authentemia,Autenticidad,Estadisticas`;
                  // console.log('[LOG_RSS] Authentemia Twitter share data:', {
                    // text,
                    // url: window.location.href,
                    // encodedUrl: url,
                    // twitterUrl,
                    // score,
                    // rarity
                  // }); // Removed for production
                  window.open(twitterUrl, '_blank');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Compartir en X
              </button>

              {/* WhatsApp */}
              <button
                onClick={() => {
                  // console.log('[LOG_RSS] Authentemia WhatsApp share button clicked'); // Removed for production
                  const text = `🎯 Mi puntuación de autenticidad es ${score}% - ¡Soy 1 en ${rarity.toLocaleString()} personas! Descubre qué tan único eres: ${window.location.href}`;
                  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                  // console.log('[LOG_RSS] Authentemia WhatsApp share data:', {
                    // text,
                    // whatsappUrl,
                    // score,
                    // rarity
                  // }); // Removed for production
                  window.open(whatsappUrl, '_blank');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                WhatsApp
              </button>

              {/* LinkedIn */}
              <button
                onClick={() => {
                  // console.log('[LOG_RSS] Authentemia LinkedIn share button clicked'); // Removed for production
                  const text = `Mi puntuación de autenticidad es ${score}% - ¡Soy 1 en ${rarity.toLocaleString()} personas! Descubre qué tan único eres con este test de autenticidad demográfica.`;
                  const url = encodeURIComponent(window.location.href);
                  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
                  // console.log('[LOG_RSS] Authentemia LinkedIn share data:', {
                    // text,
                    // url: window.location.href,
                    // encodedUrl: url,
                    // linkedinUrl,
                    // score,
                    // rarity
                  // }); // Removed for production
                  window.open(linkedinUrl, '_blank');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </button>

              {/* Facebook */}
              <button
                onClick={() => {
                  const url = encodeURIComponent(window.location.href);
                  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
                  window.open(facebookUrl, '_blank');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>

              {/* Telegram */}
              <button
                onClick={() => {
                  const text = `🎯 Mi puntuación de autenticidad es ${score}% - ¡Soy 1 en ${rarity.toLocaleString()} personas! Descubre qué tan único eres: ${window.location.href}`;
                  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(text)}`;
                  window.open(telegramUrl, '_blank');
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                Telegram
              </button>

              {/* Copy Link */}
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    // You could add a toast notification here
                    alert('¡Enlace copiado al portapapeles!');
                  });
                }}
                className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copiar Enlace
              </button>
            </div>

            <div className="text-center mt-6">
              <p className="text-sm text-muted-foreground">
                Comparte tu resultado y desafía a tus amigos a descubrir qué tan únicos son
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = data.questions[currentQuestionIndex];
  const currentAnswer = userAnswers[currentQuestion.id];

  return (
    <div className="statemia-container">

      {/* Main Content with Sidebar */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Question Section */}
        <div className="flex-1 lg:pr-96">
          <div className="statemia-card mb-6 min-h-screen lg:min-h-0">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-full text-primary">
              {getQuestionIcon(currentQuestion.id)}
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">
            {currentQuestion.label}
          </h1>
          <p className="text-lg text-muted-foreground">
            Selecciona la opción que mejor te describe
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3 mb-8">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleAnswerSelect(currentQuestion.id, option.value)}
              className={`w-full p-4 text-left rounded-lg border transition-all duration-200 ${
                currentAnswer === option.value
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-surface-300 hover:border-primary/50 hover:bg-surface-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {currentAnswer === option.value ? (
                    <CheckCircle size={20} className="text-primary" />
                  ) : (
                    <Circle size={20} className="text-muted-foreground" />
                  )}
                  <div>
                    <div className="font-semibold text-foreground">
                      {option.value}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {option.p_global}% de la población mundial
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-muted-foreground">
                    1 en {Math.round(100 / option.p_global).toLocaleString()}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Source Info and Final Button */}
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Info size={16} />
            Fuente: {currentQuestion.options[0]?.source.name}
          </div>
          
          {currentQuestionIndex === data.questions.length - 1 && currentAnswer && (
            <button
              onClick={() => setShowResults(true)}
              className="btn-primary flex items-center gap-2"
            >
              <TrendingUp size={16} />
              Ver Resultados Finales
            </button>
          )}
        </div>
      </div>
        </div>

        {/* Live Progress Sidebar */}
        <div className="w-full lg:w-80 lg:fixed lg:right-6 lg:top-32 lg:z-30 fixed bottom-0 left-0 right-0 lg:bottom-auto lg:left-auto">
          <div className="statemia-card h-fit bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t lg:border-t-0 lg:border-l border-border">
            {/* Desktop Layout */}
            <div className="hidden lg:block">
              <div className="text-center mb-6">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={handlePrevious}
                    disabled={currentQuestionIndex === 0}
                    className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Pregunta anterior"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <h3 className="text-lg font-semibold text-foreground">
                    Tu Progreso
                  </h3>
                  <div className="w-8"></div> {/* Spacer for alignment */}
                </div>
                <div className="text-3xl font-bold text-primary mb-1">
                  {getCurrentProgress().score}%
                </div>
                <div className="text-sm text-muted-foreground">
                  Puntuación de Autenticidad
                </div>
              </div>

              {/* Progress Circle */}
              <div className="flex justify-center mb-6">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="var(--surface-200)"
                      strokeWidth="8"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="var(--primary)"
                      strokeWidth="8"
                      strokeDasharray={`${(getCurrentProgress().score / 100) * 352} 352`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-foreground">
                        {getCurrentProgress().answeredCount}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        de {data?.questions.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* People Count */}
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {getCurrentProgress().peopleCount.toLocaleString()}
                </div>
                <div className="text-sm text-blue-800">
                  personas pueden ser como tú
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  de 8,000 millones en el mundo
                </div>
              </div>
            </div>

            {/* Mobile Banner Layout */}
            <div className="lg:hidden">
              <div className="flex items-center justify-between p-4">
                {/* Left: Authenticity Score */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {getCurrentProgress().score}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Autenticidad
                  </div>
                </div>

                {/* Right: People Count */}
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {getCurrentProgress().peopleCount.toLocaleString()}
                  </div>
                  <div className="text-xs text-blue-800">
                    personas como tú
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>
      
      {/* Spacer for mobile fixed sidebar */}
      <div className="h-20 lg:hidden"></div>
    </div>
  );
};

export default Authentemia; 